import React, { useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Pane, Popup, useMap } from 'react-leaflet';
import { getMarkerIcon } from './markerIcons';
import StateLabels from './StateLabels';
import usStates from '../../../data/us-states.json';
import basemap from '../../../data/basemap.json';

// Focus the default view on the core operating region (most projects live here)
// so the map reads tighter and less "zoomed out". Outliers stay pannable.
const CORE_BOUNDS = [
  [28.5, -99.0],
  [40.5, -76.0],
];
const MAX_BOUNDS = [
  [8, -140],
  [62, -50],
];

// Vector basemap (Natural Earth) keeps land/water razor-sharp on any screen
// and carries no baked-in country labels.
const LAND_STYLE = { color: '#c9c9cc', weight: 0.6, fillColor: '#efefef', fillOpacity: 1 };
const STATE_STYLE = {
  color: '#334049',
  weight: 1.1,
  opacity: 0.7,
  fillColor: '#efefef',
  fillOpacity: 1,
};
const LAKE_STYLE = { stroke: false, fillColor: '#d0cfd4', fillOpacity: 1 };

function EscapeClosesPopup() {
  const map = useMap();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') map.closePopup();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [map]);
  return null;
}

function MarkerPopup({ location }) {
  return (
    <Popup className="custom-popup" closeButton={false} minWidth={0}>
      <div data-testid="map-marker-popup">
        <p className="font-display font-semibold leading-tight" data-testid="map-popup-location">
          {location.city}{location.state ? `, ${location.state}` : ''}
        </p>
        <p className="font-mono text-[11px] opacity-75" data-testid="map-popup-job">
          Job #{location.job}
        </p>
      </div>
    </Popup>
  );
}

export default function MapCanvas({ locations, selectedId, onMarkerClick }) {
  return (
    <MapContainer
      bounds={CORE_BOUNDS}
      boundsOptions={{ padding: [20, 20] }}
      maxBounds={MAX_BOUNDS}
      maxBoundsViscosity={1}
      minZoom={4}
      maxZoom={12}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={false}
      zoomControl={true}
      className="z-0"
    >
      <GeoJSON
        data={basemap.land}
        style={() => LAND_STYLE}
        interactive={false}
        attribution="Basemap &copy; Natural Earth"
      />
      <GeoJSON data={usStates} style={() => STATE_STYLE} interactive={false} />
      <GeoJSON data={basemap.lakes} style={() => LAKE_STYLE} interactive={false} />
      {/* Esri city labels only once zoomed in; no API key required. */}
      <Pane name="city-labels" style={{ zIndex: 440, pointerEvents: 'none' }}>
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
          attribution="Labels &copy; Esri"
          minZoom={7}
          maxZoom={12}
        />
      </Pane>
      <StateLabels />
      <EscapeClosesPopup />
      {locations.map((location) => (
        <Marker
          key={location.id}
          position={[location.lat, location.lng]}
          icon={getMarkerIcon(location, selectedId)}
          title={`${location.city}${location.state ? `, ${location.state}` : ''} — Job #${location.job}`}
          alt={`Project location: ${location.city}${location.state ? `, ${location.state}` : ''}`}
          eventHandlers={{ click: () => onMarkerClick(location) }}
        >
          <MarkerPopup location={location} />
        </Marker>
      ))}
    </MapContainer>
  );
}
